import React, { useState, useCallback, useEffect, useRef } from "react";
import { Typography, Space, Button, Tag, Spin, Empty, Alert } from "antd";
import { RobotOutlined, ReloadOutlined, ClockCircleOutlined } from "@ant-design/icons";

import {
	fetchPatientAnalysis,
	triggerPatientAnalysis,
	fetchPatientAnalysisStatus,
} from "../../../../api/healthcare/PatientAnalysisApi";
import { PatientAnalysis } from "../../../../types/healthcare/PatientAnalysisTypes";
import ComprehensiveAnalysisRenderer from "../../treatmentPlans/planComponents/planDrawerComponents/workflowProgress/ComprehensiveAnalysisRenderer";
import type { UserUuid } from "../../../../types/identity";

const { Text } = Typography;

interface Props {
	patientId: UserUuid;
	company: string;
	gender?: string;
	initialStatus?: string | null;
}

const POLLING_STATUSES = ["pending", "processing", "stage_1_complete", "appraising"];

const PatientAnalysisSection: React.FC<Props> = ({ patientId, company, gender, initialStatus }) => {
	const [analysis, setAnalysis] = useState<PatientAnalysis | null>(null);
	const [loadingAnalysis, setLoadingAnalysis] = useState(true);
	const [status, setStatus] = useState<string | null>(initialStatus ?? null);
	const [triggering, setTriggering] = useState(false);

	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const loadAnalysis = useCallback(async () => {
		try {
			const data = await fetchPatientAnalysis(patientId);
			setAnalysis(data);
		} catch {
			// silently fail
		} finally {
			setLoadingAnalysis(false);
		}
	}, [patientId]);

	const loadStatus = useCallback(async () => {
		try {
			const data = await fetchPatientAnalysisStatus(patientId);
			setStatus(data.status ?? null);
			if (data.status === "completed") {
				loadAnalysis();
			}
		} catch {
			setStatus(null);
		}
	}, [patientId, loadAnalysis]);

	useEffect(() => {
		loadAnalysis();
		// Status is already known from the aggregated overview when initialStatus
		// is supplied — skip the redundant status round-trip. The polling effect
		// below keys off the seeded status for any in-progress analysis.
		if (initialStatus === undefined || initialStatus === null) {
			loadStatus();
		}
	}, [loadAnalysis, loadStatus, initialStatus]);

	useEffect(() => {
		if (status && POLLING_STATUSES.includes(status)) {
			pollRef.current = setInterval(loadStatus, 4000);
		} else {
			if (pollRef.current) {
				clearInterval(pollRef.current);
				pollRef.current = null;
			}
		}
		return () => {
			if (pollRef.current) clearInterval(pollRef.current);
		};
	}, [status, loadStatus]);

	const triggerAnalysis = useCallback(async () => {
		setTriggering(true);
		try {
			await triggerPatientAnalysis(patientId, company);
			setStatus("pending");
		} catch {
			// silently fail — backend 409s if already processing
		} finally {
			setTriggering(false);
		}
	}, [patientId, company]);

	const isProcessing = status !== null && POLLING_STATUSES.includes(status);
	const hasAnalysis = analysis?.status === "completed";

	return (
		<div style={{ padding: "4px 0" }}>
			{/* Header */}
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
				<Space size={8}>
					<RobotOutlined style={{ fontSize: 16, color: "var(--mg-primary)" }} />
					<Text strong style={{ fontSize: 14 }}>Patient Analysis</Text>
					{isProcessing && (
						<Tag icon={<ClockCircleOutlined />} color="processing">
							{status === "stage_1_complete"
								? "Analysing medications…"
								: status === "appraising"
									? "Validating analysis…"
									: "Analysing…"}
						</Tag>
					)}
					{analysis?.generated_at && !isProcessing && (
						<Text type="secondary" style={{ fontSize: 11 }}>
							{new Date(analysis.generated_at).toLocaleDateString("en-AU", {
								day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
							})}
						</Text>
					)}
				</Space>
				<Button
					size="small"
					icon={<ReloadOutlined spin={triggering || isProcessing} />}
					onClick={triggerAnalysis}
					disabled={triggering || isProcessing}
					type={hasAnalysis ? "default" : "primary"}
				>
					{hasAnalysis ? "Re-analyse" : "Generate Analysis"}
				</Button>
			</div>

			{/* Failed state */}
			{analysis?.status === "failed" && (
				<Alert
					type="error"
					message="Analysis failed"
					description={analysis.error_message ?? "An error occurred during analysis."}
					showIcon
					style={{ marginBottom: 12 }}
				/>
			)}

			{/* Loading / processing */}
			{(loadingAnalysis || isProcessing) && !hasAnalysis && (
				<div style={{ textAlign: "center", padding: 32 }}>
					<Spin size="large" />
					<div style={{ marginTop: 12 }}>
						<Text type="secondary">
							{loadingAnalysis ? "Loading analysis…" : "AI is analysing patient data…"}
						</Text>
					</div>
				</div>
			)}

			{/* No analysis yet */}
			{!loadingAnalysis && !isProcessing && !hasAnalysis && analysis?.status !== "failed" && (
				<Empty
					image={<RobotOutlined style={{ fontSize: 48, color: "var(--mg-border)" }} />}
					description={
						<Space direction="vertical" size={4}>
							<Text type="secondary">No analysis generated yet.</Text>
							<Text type="secondary" style={{ fontSize: 11 }}>
								Click "Generate Analysis" to run a full clinical analysis for this patient.
							</Text>
						</Space>
					}
					style={{ padding: "24px 0" }}
				/>
			)}

			{/* Completed analysis */}
			{hasAnalysis && (
				<ComprehensiveAnalysisRenderer
					analysisId={analysis.id}
					planId={0}
					preloadedAnalysis={analysis}
					showReviewButton={false}
					patientGender={gender}
				/>
			)}
		</div>
	);
};

export default PatientAnalysisSection;
